/**
 * @fileoverview Checkbox question component for rendering checkbox questions
 *
 * @example
 * // Usage in component:
 * import { CheckboxQuestion } from './Checkbox'
 *
 * <CheckboxQuestion onSubmit={onSubmit} question={question} />
 */

import { Instructions } from './sections/Instructions'

import { useTranslation } from 'react-i18next'
import { NavigationButton } from '../NavigationButton'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { Text } from '../typography/Typography'
import css from './Checkbox.module.css'
import { List } from './sections/List'
import { Title } from './sections/Title'

/**
 * @function CheckboxQuestion
 * @description Component for rendering checkbox question with progress indicators and navigation buttons
 * @param {Novoic.CheckboxQuestionProps} props - The props for the component
 * @returns {JSX.Element} The CheckboxQuestion component
 */
export function CheckboxQuestion({ onSubmit, question }: Novoic.CheckboxQuestionProps) {
    const { id, question: body } = question
    const { title, accent, instructions, list, subtext, checkboxes } = body
    const { t } = useTranslation()

    const state = checkboxes.map((checkbox) => ({
        ...checkbox,
        value: checkbox.default,
        pristine: true,
    }))

    function onConfirm() {
        return onSubmit(id, {
            checkboxes: state.reduce((acc, checkbox) => {
                acc[checkbox.name] = checkbox.value
                return acc
            }, {} as Record<string, boolean>),
        })
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title accent={accent}>{t(title)}</Title>
            {subtext && <Text>{t(subtext)}</Text>}
            {instructions && <Instructions instructions={t(instructions)} />}
            {list && <List list={list} />}

            <div className={css.button_container}>
                <DesktopProgress />
                <NavigationButton type="confirm" label={body.button} onClick={onConfirm} />
            </div>
        </div>
    )
}
